import React, { useState } from 'react';

import { Autocomplete, TextField, Typography, useTheme } from '@mui/material';
import type { GridFilterInputValueProps } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { AlineCheckbox, FilterTags, PartyAvatar } from 'src/components';
import {
  PartyListboxPaginationProvider,
  VirtualizedPartyListbox,
} from 'src/components/parties/VirtualizedPartyListbox';
import type { Apollo } from 'src/gql/generated';
import { usePartySearch } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import {
  filterAutocompleteStyle,
  usePreserveAutocompleteSearchInput,
} from './autocomplete-filter-utils';

const useStyles = makeStyles()(theme => ({
  autocomplete: filterAutocompleteStyle,
  partyItem: {
    alignItems: 'center',
    display: 'flex',
  },
  partyText: {
    marginLeft: theme.spacing(1),
  },
}));

// Use Partial b/c we have no guarantee of the object shape coming out of apiRef.getRowModels.
export type PartyFilterOption = Partial<Pick<Apollo.Party, 'id' | 'name' | '__typename'>>;

export function PartiesAutocompleteFilter({
  applyValue,
  item,
}: GridFilterInputValueProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const [open, setOpen] = useState(false);
  // Skip the search until the dropdown is opened so it doesn't fire on mount.
  const {
    hasMore,
    loadMore,
    loading,
    parties: sortedOptions,
    setSearch,
  } = usePartySearch({ skip: !open });
  const [truncateTags, setTruncateTags] = useState(true);
  const [value, setValue] = useState<PartyFilterOption[]>(item.value ?? []);
  // Keeps the typed search text from clearing when a checkbox is toggled.
  const { inputValue, onInputChange: preserveInputChange } = usePreserveAutocompleteSearchInput();

  function handleValueChange(value: PartyFilterOption[]): void {
    applyValue({
      ...item,
      value,
    });
    setValue(value);
  }

  return (
    <PartyListboxPaginationProvider hasMore={hasMore} loadMore={loadMore} loading={loading}>
      <Autocomplete<PartyFilterOption, true, false, false>
        disableCloseOnSelect
        disableListWrap
        fullWidth
        multiple
        openOnFocus
        blurOnSelect={false}
        className={classes.autocomplete}
        // Options are filtered server-side by the search term; pass them through.
        filterOptions={options => options}
        getOptionKey={option => option.id ?? ''}
        getOptionLabel={option => option.name ?? ''}
        inputValue={inputValue}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        open={open}
        options={sortedOptions}
        renderInput={params => {
          return <TextField placeholder="Search parties..." {...params} variant="standard" />;
        }}
        renderOption={(props, option, state) => {
          const { className, key, ...otherProps } = props;
          return (
            <li key={key} {...otherProps} className={clsx(className, classes.partyItem)}>
              <AlineCheckbox checked={state.selected} style={{ marginRight: theme.spacing(1) }} />
              <PartyAvatar party={option} />
              <Typography noWrap className={classes.partyText}>
                {option.name}
              </Typography>
            </li>
          );
        }}
        renderValue={tags => {
          return (
            <FilterTags<PartyFilterOption>
              getTextFromTag={tag => tag.name ?? ''}
              tags={tags}
              truncateTags={truncateTags}
              onDelete={tag => handleValueChange(tags.filter(({ id }) => tag.id !== id))}
            />
          );
        }}
        slotProps={{
          listbox: {
            // Strip the default listbox padding so react-window's rows line up.
            sx: { '& > ul': { margin: 0, padding: 0 }, padding: 0 },
          },
          popper: {
            modifiers: [
              {
                enabled: false,
                name: 'flip',
              },
              {
                enabled: false,
                name: 'preventOverflow',
              },
            ],
          },
        }}
        slots={{ listbox: VirtualizedPartyListbox }}
        value={value}
        onBlur={e => {
          e.stopPropagation();
          setTruncateTags(true);
        }}
        onChange={(_e, value) => handleValueChange(value)}
        onClose={() => setOpen(false)}
        onFocus={e => {
          e.stopPropagation();
          setTruncateTags(false);
        }}
        onInputChange={(event, newInputValue, reason) => {
          preserveInputChange(event, newInputValue, reason);
          /**
           * Preserve the search on select/remove/reset (mirrors the hook) so
           * toggling a checkbox doesn't re-run the server search.
           */
          if (reason === 'reset' || reason === 'selectOption' || reason === 'removeOption') return;
          setSearch(newInputValue);
        }}
        onOpen={() => setOpen(true)}
      />
    </PartyListboxPaginationProvider>
  );
}
